import { z } from 'zod';
import { NumberFormatting } from '@mews-framework/globalization';
import { NumberService } from '@mews-framework/services';
import { createCultureFormatRegex } from '../cultureFormatRegex';
import { CustomErrorParams, CustomErrorType } from '../CustomErrorType';

const createCurrencySchema = (params: CustomErrorParams) => (numberLocalizationInfo: NumberFormatting) => {
    const cultureFormatRegex = createCultureFormatRegex(numberLocalizationInfo);
    return z.number().or(
        z
            .string()
            .refine((v) => cultureFormatRegex.test(v), { params: params satisfies CustomErrorParams })
            .transform((value) => NumberService.unformat(value, numberLocalizationInfo))
            .refine((value) => !Number.isNaN(value), { params: params satisfies CustomErrorParams })
    );
};

export const createLocalizedCurrencyNumberSchema = createCurrencySchema({ errorType: CustomErrorType.FormattedNumber });

type NumberComparator = (
    value: number,
    message?:
        | string
        | {
              message?: string;
          }
) => z.ZodNumber;

export const createLocalizedComparatorSchema = ({
    numberCultureInfo,
    numberComparator,
    threshold,
    params,
}: {
    numberCultureInfo: NumberFormatting;
    numberComparator: NumberComparator;
    threshold: string;
    params: Extract<CustomErrorParams, { errorType: CustomErrorType.Other }>;
}) => {
    const currencySchema = createCurrencySchema(params)(numberCultureInfo);
    const parsedThreshold = currencySchema.safeParse(threshold);

    let thresholdNumber = 0;

    if (parsedThreshold.success) {
        thresholdNumber = parsedThreshold.data;
    } else {
        return z.NEVER;
    }

    return currencySchema.pipe(numberComparator(thresholdNumber, params.textKey));
};
